import { SIJI_DATA } from '@entities/saju'
import type { BirthInfoValues } from '@shared/lib/birth-info-schema'
import { birthInfoSchema } from '@shared/lib/birth-info-schema'
import { cn } from '@shared/lib/cn'
import type { SavedUserInfo } from '@shared/lib/saved-user-info'
import { Button } from '@shared/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@shared/ui/drawer'
import { Input } from '@shared/ui/input'
import { useForm, useStore } from '@tanstack/react-form'
import { useEffect, useRef } from 'react'
import { HiMiniCheck } from 'react-icons/hi2'

interface SavedInfoDrawerProps {
  readonly isOpen: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly mode: 'add' | 'edit'
  readonly initialData: SavedUserInfo | null
  readonly onSave: (info: Omit<SavedUserInfo, 'savedAt'>) => void
}

function getDefaults(data: SavedUserInfo | null): BirthInfoValues {
  return {
    gender: data?.gender ?? '',
    name: data?.name ?? '',
    birthYear: data?.birthYear ?? '',
    birthMonth: data?.birthMonth ?? '',
    birthDay: data?.birthDay ?? '',
    birthTime: data?.birthTime ?? '',
    isLunar: data?.isLunar ?? false,
  }
}

export function SavedInfoDrawer({
  isOpen,
  onOpenChange,
  mode,
  initialData,
  onSave,
}: SavedInfoDrawerProps) {
  const monthRef = useRef<HTMLInputElement>(null)
  const dayRef = useRef<HTMLInputElement>(null)

  const form = useForm({
    defaultValues: getDefaults(initialData),
    validators: {
      onChange: birthInfoSchema,
    },
    onSubmit: ({ value }) => {
      onSave({
        gender: value.gender as 'MALE' | 'FEMALE',
        name: value.name,
        birthYear: value.birthYear,
        birthMonth: value.birthMonth,
        birthDay: value.birthDay,
        birthTime: value.birthTime,
        isLunar: value.isLunar,
      })
    },
  })

  const v = useStore(form.store, (s) => s.values)
  const canSubmit = useStore(form.store, (s) => s.canSubmit)

  // Reset form when drawer opens
  useEffect(() => {
    if (isOpen) {
      form.reset(getDefaults(initialData))
    }
  }, [isOpen, initialData, form])

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{mode === 'add' ? '내 정보 등록' : '내 정보 수정'}</DrawerTitle>
          <DrawerDescription>사주 분석을 위한 기본 정보를 입력해주세요</DrawerDescription>
        </DrawerHeader>

        <form
          noValidate
          className="flex flex-col gap-5 overflow-y-auto px-4 pb-2"
          onSubmit={(e) => {
            e.preventDefault()
            void form.handleSubmit()
          }}
        >
          {/* Gender */}
          <div className="flex flex-col gap-2">
            <span className="typo-caption font-medium text-muted-foreground">성별</span>
            <div className="flex gap-2.5">
              {[
                { label: '남자', value: 'MALE' },
                { label: '여자', value: 'FEMALE' },
              ].map((opt) => {
                const isSelected = v.gender === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(
                      'flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl py-3.5 typo-label font-semibold transition-all',
                      isSelected
                        ? 'border border-primary bg-primary/10 text-foreground'
                        : 'border border-border bg-bg-surface text-muted-foreground',
                    )}
                    onClick={() => {
                      form.setFieldValue('gender', opt.value as 'MALE' | 'FEMALE')
                    }}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Name */}
          <div className="flex flex-col gap-2">
            <span className="typo-caption font-medium text-muted-foreground">이름</span>
            <Input
              placeholder="이름을 입력해주세요"
              className="h-12 rounded-xl typo-body"
              value={v.name}
              onChange={(e) => {
                form.setFieldValue('name', e.target.value)
              }}
            />
          </div>

          {/* Birthday */}
          <div className="flex flex-col gap-2">
            <span className="typo-caption font-medium text-muted-foreground">생년월일</span>
            <div className="flex gap-2">
              <Input
                type="tel"
                inputMode="numeric"
                placeholder="YYYY"
                maxLength={4}
                className="h-12 flex-[2] rounded-xl text-center font-mono typo-body"
                value={v.birthYear}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 4)
                  form.setFieldValue('birthYear', val)
                  if (val.length === 4) {
                    monthRef.current?.focus()
                  }
                }}
              />
              <Input
                ref={monthRef}
                type="tel"
                inputMode="numeric"
                placeholder="MM"
                maxLength={2}
                className="h-12 flex-1 rounded-xl text-center font-mono typo-body"
                value={v.birthMonth}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 2)
                  form.setFieldValue('birthMonth', val)
                  if (val.length === 2) {
                    dayRef.current?.focus()
                  }
                }}
              />
              <Input
                ref={dayRef}
                type="tel"
                inputMode="numeric"
                placeholder="DD"
                maxLength={2}
                className="h-12 flex-1 rounded-xl text-center font-mono typo-body"
                value={v.birthDay}
                onChange={(e) => {
                  form.setFieldValue('birthDay', e.target.value.replace(/\D/g, '').slice(0, 2))
                }}
              />
            </div>

            {/* Solar / Lunar toggle */}
            <div className="flex gap-2">
              {[
                { label: '양력', value: false },
                { label: '음력', value: true },
              ].map((opt) => {
                const isSelected = v.isLunar === opt.value
                return (
                  <button
                    key={opt.label}
                    type="button"
                    className={cn(
                      'flex h-10 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-xl typo-label font-medium transition-all',
                      isSelected
                        ? 'border border-primary bg-primary/10 text-foreground'
                        : 'border border-border bg-bg-surface text-muted-foreground',
                    )}
                    onClick={() => {
                      form.setFieldValue('isLunar', opt.value)
                    }}
                  >
                    {isSelected && <HiMiniCheck className="text-primary" />}
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Birth Time - 4x3 grid */}
          <div className="flex flex-col gap-2">
            <span className="typo-caption font-medium text-muted-foreground">출생시간</span>
            <div className="grid grid-cols-4 gap-1.5">
              {SIJI_DATA.map((siji) => {
                const isSelected = v.birthTime === siji.value
                return (
                  <button
                    key={siji.value}
                    type="button"
                    className={cn(
                      'flex cursor-pointer flex-col items-center justify-center gap-0.5 rounded-xl py-2.5 transition-all',
                      isSelected
                        ? 'border border-primary bg-primary/10 text-foreground'
                        : 'border border-border bg-bg-surface text-muted-foreground hover:bg-bg-surface-hover',
                    )}
                    onClick={() => {
                      form.setFieldValue('birthTime', v.birthTime === siji.value ? '' : siji.value)
                    }}
                  >
                    <span className="typo-caption font-semibold">{siji.range}</span>
                    <span
                      className={cn(
                        'typo-footnote',
                        isSelected ? 'text-primary' : 'text-muted-foreground/60',
                      )}
                    >
                      {siji.name}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </form>

        <DrawerFooter className="flex-row">
          <DrawerClose asChild>
            <Button type="button" variant="outline" size="lg">
              취소
            </Button>
          </DrawerClose>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="flex-1"
            disabled={!canSubmit}
            onClick={() => {
              void form.handleSubmit()
            }}
          >
            저장하기
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
